import { Label } from "layout/typography/Label";
import { SelectButton } from "primereact/selectbutton";
import { BenefitTierPreference } from "types/BenefitSelection/BenefitSelection";

import { TIER_CHOICES } from "./constants";

interface ITierPreferenceControlProps {
  /** Also the control's `data-testid`, so the two placements are told apart in a test. */
  id: string;
  value: BenefitTierPreference;
  disabled?: boolean;
  onChange: (value: BenefitTierPreference) => void;
}

/**
 * CLE-2792 — which tier a service line is read at where the payer tiered the plan.
 *
 * 🚨 THE VALUE WAS ALWAYS ON THE ROW AND THE SCREEN NEVER OFFERED IT. `AccountBenefitMap
 * .tierPreference` is on every map row and the `PUT` has accepted it on the service line and on
 * each exception since the endpoint was written, but nothing here could set either one: an account
 * whose payer tiers its plans read `Tier2` because that is what the seeder wrote, and changing it
 * meant an UPDATE by hand.
 *
 * ⚠️ A PAYER FILTER THAT SETS A TIER BEATS THIS FOR ITS OWN PAYERS, which is what the helper line
 * says. The two are not alternatives — this is the account's tier for every payer that no filter
 * names — so the sentence states the precedence rather than warning about it.
 *
 * `SelectButton`, matching the tie-break control in `LineSelectionDialog`: two mutually exclusive
 * values, both worth showing at once. `e.value` is null when somebody clicks the selected segment,
 * and that is ignored — a service line is always read at one tier or the other.
 */
export function TierPreferenceControl({
  id,
  value,
  disabled,
  onChange,
}: ITierPreferenceControlProps) {
  return (
    <div className="flex flex-column gap-2" data-testid={id}>
      <Label htmlFor={id}>Tier to read when the payer tiers a plan</Label>
      <SelectButton
        id={id}
        data-testid={`${id}.select`}
        options={TIER_CHOICES}
        optionLabel="label"
        optionValue="value"
        value={value}
        disabled={disabled}
        onChange={(e) => e.value && onChange(e.value)}
      />
      <span className="text-xs text-color-secondary">
        A payer filter that sets a tier wins for its payers.
      </span>
    </div>
  );
}
